import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { toast } from "sonner";
import {
    ArrowLeft,
    ArrowRight,
    Check,
    Clock,
    Lock,
    PlayCircle,
} from "lucide-react";
import { coursesApi, lessonsApi, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function LessonViewerPage() {
    const { courseSlug, lessonId } = useParams();
    const navigate = useNavigate();
    const { isAuthenticated } = useAuth();

    const [lesson, setLesson] = useState(null);
    const [course, setCourse] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [completing, setCompleting] = useState(false);

    const refresh = () => {
        setLoading(true);
        Promise.all([lessonsApi.get(lessonId), coursesApi.get(courseSlug)])
            .then(([l, c]) => {
                setLesson(l);
                setCourse(c);
            })
            .catch((e) => {
                const st = e?.response?.status;
                if (st === 401) setError("auth");
                else if (st === 403) setError("locked");
                else if (st === 404) setError("not_found");
                else setError("unknown");
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        refresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lessonId, courseSlug, isAuthenticated]);

    const goTo = (lid) => navigate(`/portal/learn/${courseSlug}/${lid}`);

    const markComplete = async () => {
        if (!lesson || completing) return;
        setCompleting(true);
        try {
            await lessonsApi.complete(lesson.id);
            toast.success("Lesson complete.");
            if (lesson.next_lesson_id) {
                goTo(lesson.next_lesson_id);
            } else {
                toast.success("Course finished. Great work.");
                navigate("/portal/dashboard");
            }
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
        } finally {
            setCompleting(false);
        }
    };

    if (loading) {
        return (
            <div
                className="grid min-h-[calc(100vh-72px)] place-items-center bg-brand-offwhite font-mono-tag text-brand-steel"
                data-testid="lesson-loading"
            >
                LOADING LESSON…
            </div>
        );
    }

    if (error === "locked") {
        return (
            <div
                className="container-x grid min-h-[calc(100vh-72px)] place-content-center py-20 text-center"
                data-testid="lesson-locked"
            >
                <Lock className="mx-auto h-10 w-10 text-brand-gold" />
                <h1 className="mt-6 font-display text-5xl">ENROLL TO CONTINUE</h1>
                <p className="mt-4 font-body text-brand-steel">
                    This lesson is only available to enrolled students.
                </p>
                <Link
                    to={`/portal/courses/${courseSlug}`}
                    className="mx-auto mt-8 inline-flex btn-primary"
                    data-testid="lesson-locked-back"
                >
                    Back to course
                </Link>
            </div>
        );
    }

    if (error === "not_found") {
        return (
            <div className="container-x py-40" data-testid="lesson-not-found">
                <p className="font-mono-tag text-brand-gold">[ 404 ]</p>
                <h1 className="mt-6 font-display text-6xl">LESSON NOT FOUND</h1>
                <Link
                    to={`/portal/courses/${courseSlug}`}
                    className="mt-8 inline-block font-mono text-xs uppercase tracking-[0.25em] text-brand-charcoal hover:text-brand-gold"
                >
                    ← Back to course
                </Link>
            </div>
        );
    }

    if (!lesson || !course) return null;

    return (
        <div
            className="grid min-h-[calc(100vh-72px)] bg-brand-offwhite md:grid-cols-[300px_1fr]"
            data-testid="lesson-viewer-page"
        >
            {/* Sidebar: lesson list */}
            <aside className="hidden border-r border-brand-line bg-white md:block">
                <div className="sticky top-[72px] max-h-[calc(100vh-72px)] overflow-y-auto p-6">
                    <Link
                        to={`/portal/courses/${courseSlug}`}
                        className="inline-flex items-center gap-2 font-mono-tag text-brand-steel hover:text-brand-gold"
                        data-testid="lesson-back-to-course"
                    >
                        <ArrowLeft className="h-3 w-3" />
                        {course.title}
                    </Link>
                    <p className="mt-6 font-mono-tag text-brand-gold">
                        [ Lessons ]
                    </p>
                    <ul className="mt-4 flex flex-col gap-1">
                        {course.lessons.map((l) => {
                            const isActive = l.id === lesson.id;
                            const locked = !course.is_enrolled && !l.is_free;
                            const Icon = l.completed
                                ? Check
                                : locked
                                  ? Lock
                                  : PlayCircle;
                            return (
                                <li key={l.id}>
                                    <button
                                        type="button"
                                        disabled={locked && !isActive}
                                        onClick={() => !locked && goTo(l.id)}
                                        className={`flex w-full items-start gap-3 border-l-2 p-3 text-left transition-colors ${
                                            isActive
                                                ? "border-brand-gold bg-brand-gold/10"
                                                : "border-transparent hover:bg-brand-offwhite"
                                        } ${locked ? "cursor-not-allowed opacity-60" : ""}`}
                                        data-testid={`lesson-sidebar-item-${l.id}`}
                                    >
                                        <Icon className={`mt-1 h-4 w-4 flex-shrink-0 ${
                                            l.completed
                                                ? "text-brand-gold"
                                                : isActive
                                                  ? "text-brand-charcoal"
                                                  : "text-brand-steel"
                                        }`} />
                                        <div>
                                            <p className="font-mono-tag text-brand-steel">
                                                {String(l.order).padStart(2, "0")}
                                            </p>
                                            <p className="mt-0.5 font-body text-sm leading-snug text-brand-charcoal">
                                                {l.title}
                                            </p>
                                            <p className="mt-1 font-mono-tag text-[9px] text-brand-steel">
                                                {l.duration_minutes} min
                                            </p>
                                        </div>
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            </aside>

            {/* Content */}
            <div className="container-x py-12 md:py-16">
                <div className="mx-auto max-w-3xl">
                    <div
                        className="flex items-center gap-3"
                        data-testid="lesson-header"
                    >
                        <p className="font-mono-tag text-brand-gold">
                            [ LESSON {lesson.order} / {lesson.total_lessons} ]
                        </p>
                        <span className="flex items-center gap-2 font-mono-tag text-brand-steel">
                            <Clock className="h-3 w-3" />
                            {lesson.duration_minutes} min
                        </span>
                    </div>
                    <h1
                        className="mt-4 font-display text-5xl leading-[1.05] md:text-6xl"
                        data-testid="lesson-title"
                    >
                        {lesson.title}
                    </h1>

                    {lesson.video_url && (
                        <div className="mt-10 aspect-video border border-brand-line" data-testid="lesson-video">
                            <iframe
                                src={lesson.video_url}
                                title={lesson.title}
                                allowFullScreen
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                className="h-full w-full"
                            />
                        </div>
                    )}

                    <article
                        className="prose prose-lg mt-10 max-w-none font-body text-brand-charcoal prose-headings:font-display prose-headings:uppercase prose-headings:tracking-wide prose-h3:text-2xl prose-h3:text-brand-charcoal prose-p:text-brand-steel prose-p:leading-relaxed prose-strong:text-brand-charcoal prose-a:text-brand-gold prose-ul:text-brand-steel prose-ol:text-brand-steel prose-li:my-1"
                        data-testid="lesson-content"
                    >
                        <ReactMarkdown>{lesson.content_md}</ReactMarkdown>
                    </article>

                    <div className="mt-16 flex flex-col items-start justify-between gap-4 border-t border-brand-line pt-8 md:flex-row md:items-center">
                        <div>
                            {lesson.prev_lesson_id ? (
                                <button
                                    type="button"
                                    onClick={() => goTo(lesson.prev_lesson_id)}
                                    className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-brand-charcoal hover:text-brand-gold"
                                    data-testid="lesson-prev-button"
                                >
                                    <ArrowLeft className="h-4 w-4" /> Previous lesson
                                </button>
                            ) : (
                                <span />
                            )}
                        </div>
                        <div className="flex flex-col items-start gap-3 md:flex-row md:items-center">
                            {lesson.completed && (
                                <span
                                    className="inline-flex items-center gap-2 font-mono-tag text-brand-gold"
                                    data-testid="lesson-completed-badge"
                                >
                                    <Check className="h-3 w-3" /> COMPLETED
                                </span>
                            )}
                            {course.is_enrolled ? (
                                <button
                                    type="button"
                                    onClick={markComplete}
                                    disabled={completing}
                                    className="btn-primary disabled:opacity-60"
                                    data-testid="lesson-complete-button"
                                >
                                    {completing
                                        ? "Saving…"
                                        : lesson.completed
                                          ? "Continue"
                                          : "Mark complete & continue"}
                                    <ArrowRight className="h-4 w-4" />
                                </button>
                            ) : (
                                <Link
                                    to={`/portal/courses/${courseSlug}`}
                                    className="btn-primary"
                                    data-testid="lesson-enroll-cta"
                                >
                                    Enroll to complete
                                    <ArrowRight className="h-4 w-4" />
                                </Link>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
